import { Button } from '@warp-ds/react/components/button';
import cn from 'classnames';
import { type ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CloseButton } from '../../../components/warp/closeButton/CloseButton';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { useEscapeToClose } from '../../../hooks/useEscapeToClose';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { usePreventScroll } from '../../../hooks/usePreventScroll';
import styles from './FrontpageModal.module.css';
import { TriggerButton, type TriggerProps } from './TriggerButton';

export interface FrontpageModal {
    trigger: {
        text: TriggerProps['text'];
        prefix?: TriggerProps['prefix'];
        aria?: {
            label?: string;
        };
    };
    breakpoint?: 'sm' | 'md' | 'lg';
    children?: ReactNode;
    title: string;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    hideChevron?: boolean;
    error?: string;
    showFooter?: boolean;
}

interface FrontpageModalHeaderProps {
    title?: string;
    onClose: (open: boolean) => void;
}

export const FrontpageModalHeader = ({ title, onClose }: FrontpageModalHeaderProps) => (
    <header className={cn(styles.modal__header)}>
        {title && <h1 className={styles.modal__header__title}>{title}</h1>}
        <CloseButton onClose={onClose} />
    </header>
);

export const FrontpageModalFooter = ({ onClose }: { onClose: (open: boolean) => void }) => (
    <footer className={styles.modal__footer}>
        <Button primary fullWidth type="button" onClick={() => onClose(false)}>
            Bruk
        </Button>
    </footer>
);

export const FrontpageModal = ({
    breakpoint,
    children,
    trigger,
    title,
    open: controlledOpen,
    onOpenChange,
    hideChevron,
    error,
    showFooter = true,
}: FrontpageModal) => {
    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const open = isControlled ? controlledOpen : internalOpen;

    const closeModal = () => {
        if (isControlled) {
            onOpenChange?.(false);
        } else {
            setInternalOpen(false);
        }
    };

    const toggleOpen = () => {
        if (isControlled) {
            onOpenChange?.(!open);
        } else {
            setInternalOpen((prev) => !prev);
        }
    };

    const modalRef = useRef<HTMLDivElement>(null);

    const matchesBreakpoint = useBreakpoints({ breakpoint: breakpoint ?? 'sm' });
    useFocusTrap(open, modalRef);
    useEscapeToClose(open, closeModal);
    usePreventScroll(open && matchesBreakpoint);

    const modal = (
        <div
            {...(open && {
                role: 'dialog',
                'aria-modal': true,
                'aria-labelledby': 'modal-title',
            })}
            ref={modalRef}
            aria-hidden={!open}
            className={cn(styles.modal, {
                [styles['modal--open']]: open,
                [styles['modal--close']]: !open,
            })}>
            <FrontpageModalHeader title={title} onClose={closeModal} />
            <div className={styles.modal__content}>{children}</div>
            {showFooter && <FrontpageModalFooter onClose={closeModal} />}
        </div>
    );

    return (
        <div
            className={cn(styles.wrapper, {
                [styles[`wrapper--${breakpoint}`]]: breakpoint,
            })}>
            <TriggerButton
                onTrigger={toggleOpen}
                text={trigger.text}
                prefix={trigger.prefix}
                hideChevron={hideChevron}
                error={error}
                aria={{
                    label: trigger.aria?.label,
                    hasPopup: 'dialog',
                }}
            />
            {open && (matchesBreakpoint ? createPortal(modal, document.body) : modal)}
        </div>
    );
};
